import { paymentAPI, countriesAPI } from '../services/api';
import { findActiveCountry } from './countrySelection';

/**
 * Given a user's payment history and the active-countries list, decide whether there is
 * exactly one sensible country to recover `state.selectedCountry` from.
 *
 * Mirrors the same completed / non-reservation filter ApplicationsPage.jsx's fetchApplications
 * effect already applies, so recovery agrees with what "Başvurularım" shows. Pure and
 * side-effect free (no network, no dispatch) so it's unit-testable without mocking the API layer.
 *
 * Returns the country slug to recover, or `null` when there's no completed application, or the
 * signal is ambiguous (2+ distinct countries) — callers must never guess in that case, matching
 * the "do NOT guess a country when the user genuinely has none" rule.
 */
export function resolveRecoverableCountrySlug(paymentHistory, countries) {
  if (!Array.isArray(paymentHistory) || paymentHistory.length === 0) return null;

  const completedPayments = paymentHistory.filter((p) =>
    p?.paymentStatus === 'Completed' && (p.country || '').toLowerCase() !== 'reservation'
  );

  const slugs = new Set();
  for (const payment of completedPayments) {
    const countryKey = (payment.country || '').toLowerCase();
    // Unassigned standalone-reservation payments carry no real country yet (the user still has
    // to pick one on ApplicationsPage) — not a "sensible candidate" to auto-select here.
    if (!countryKey || countryKey === 'unassigned') continue;
    const countryData = findActiveCountry(countries, countryKey);
    slugs.add(countryData?.slug || countryKey);
  }

  // Exactly one distinct country → safe to recover. Zero or 2+ → ambiguous/none; let the user
  // pick explicitly rather than guessing.
  return slugs.size === 1 ? [...slugs][0] : null;
}

/**
 * Best-effort server-side recovery of `state.selectedCountry` for an authenticated user whose
 * browser has no locally-stored country (fresh browser / cleared storage / different device).
 * Reuses the same two calls ApplicationsPage.jsx already makes on mount.
 *
 * Callers are expected to treat this as fail-open (wrap in try/catch, never block rendering) —
 * see AppContext.jsx's welcome-status / notification-package checks for the established pattern
 * this follows.
 */
export async function fetchRecoverableCountrySlug() {
  const [paymentHistory, countriesResponse] = await Promise.all([
    paymentAPI.getUserPaymentHistoryViaBackend(),
    countriesAPI.getActiveCountries(),
  ]);
  const countries = countriesResponse?.success && countriesResponse.countries ? countriesResponse.countries : [];
  return resolveRecoverableCountrySlug(paymentHistory, countries);
}
